import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Redirect } from 'react-router';
import queryString from 'query-string';

import { SCREEN_ID_FEED_CATEGORY } from 'client/constants/screenIds';
import { FEED_CATEGORY_EXCLUSIVE_APARTMENTS } from 'common/feedCategoryConstants';
import {
  findFeedCategoryId,
  actionInitializeFeedCategoryState,
  actionFeedCategoryLoadlistings,
  shouldFeedCategoryLoadInitialListings,
} from 'client/helpers/feedCategoryHelpers';
import {
  getCurrentUserRenterProfile,
} from 'common/householdHelpers';
import analytics from 'client/analytics';
import {
  ANALYTICS_EVENT_LISTING_CATEGORY_VIEWED,
} from 'client/analytics/eventList';

import {
  SR_API_ACTION_CANCEL_APPLICATION_WTA_FOLLOW_UPS,
} from 'api/actionTypes';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import ListingActions from 'client/actions/Listing';
import postWithState from 'client/actions/postWithState';
import branch from 'client/core/branch';

import SrNavbar from 'client/components/SrNavbar';
import SrLink from 'client/components/SrLink/SrLink';
import NavbarButton from 'client/components/NavbarButton';
import TabBar from 'client/components/TabBar';
import SrFeedCategoryListView from 'client/components/SrFeedCategoryListView';
import SrBrowseBestListingsModal from 'client/components/SrBrowseBestListingsModal/SrBrowseBestListingsModal';

@withRouter
@withTransitionHook
@withScreenId(SCREEN_ID_FEED_CATEGORY)
@withScrollToTop
@branch({
  currentUser: ['currentUser'],
  renterProfilesByUserId: ['household', 'renterProfilesByUserId'],
  currentUserMarket: ['markets', 'currentUserMarket'],
})
class SrListviewPage extends Component {
  static fetchData(match, inject) {
    const fetchDataPromises = [
      ListingActions.getTotalListingsCount(inject),
    ];

    const feedCategoryId = findFeedCategoryId(match.params.feedCategoryId);
    if (feedCategoryId) {
      actionInitializeFeedCategoryState(inject, { feedCategoryId });

      if (shouldFeedCategoryLoadInitialListings(inject, { feedCategoryId })) {
        fetchDataPromises.push(actionFeedCategoryLoadlistings(inject, { feedCategoryId }));
      }
    }

    if (fetchDataPromises.length) {
      return Promise.all(fetchDataPromises);
    }
  }

  static getPageMetadata = () => ({
    pageTitle: 'Rent the best homes in Texas | Sunroom Rentals',
    section: 'Listings',
    keywords: 'rent, home, austin, tour, apartment',
    description: (
      'Spend less time scheduling tours, our rental experts will schedule ' +
      'and confirm tours for you.'
    ),
    image: 'https://sunroomrentals.com/images/metadata/og-house.jpg',
  })

  static propTypes = {
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    renterProfilesByUserId: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
    currentUserMarket: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);

    this.state = {
      showBrowseBestListingsModal: false,
    };
  }

  componentDidMount() {
    const {
      history,
      match,
      dispatch,
    } = this.props;

    analytics.track(ANALYTICS_EVENT_LISTING_CATEGORY_VIEWED, {
      feedCategoryId: findFeedCategoryId(match.params.feedCategoryId),
    });

    const queryUrl = queryString.parse(location.search);
    if (queryUrl.notInterestedListingId) {
      this.setState({ showBrowseBestListingsModal: true });
      history.replace(match.url);
      dispatch(postWithState, {
        action: SR_API_ACTION_CANCEL_APPLICATION_WTA_FOLLOW_UPS,
        payload: {
          listingId: Number(queryUrl.notInterestedListingId),
        },
      }).catch(() => true);
    }
  }

  _closeBrowseBestListingsModal = () => {
    this.setState({ showBrowseBestListingsModal: false });
  }

  render() {
    const {
      history,
      match,
      currentUser,
      currentUserMarket,
    } = this.props;

    const { showBrowseBestListingsModal } = this.state;

    const feedCategoryId = findFeedCategoryId(match.params.feedCategoryId);
    const renterProfile = getCurrentUserRenterProfile(this.props);

    if (!feedCategoryId) {
      return <Redirect to='/feed' />;
    }

    return (
      <div className='SrPage SrListviewPage'>
        <SrNavbar
          leftButton={(
            <SrLink
              className='back link'
              to='/feed'
              restoreScrollPosition
            >
              Back
            </SrLink>
          )}
          rightButton={feedCategoryId !== FEED_CATEGORY_EXCLUSIVE_APARTMENTS && (
            <NavbarButton
              onClick={() => history.push(`/feed/${feedCategoryId}/map`)}
            >
              Map
            </NavbarButton>
          )}
        />
        <SrFeedCategoryListView
          feedCategoryId={feedCategoryId}
          currentUser={currentUser}
          renterProfile={renterProfile}
          history={history}
          market={currentUserMarket}
        />
        <TabBar />
        {showBrowseBestListingsModal && (
          <SrBrowseBestListingsModal
            onClose={this._closeBrowseBestListingsModal}
            headline={'We\'ve stopped sending you emails about that property.'}
          />
        )}
      </div>
    );
  }
}

SrListviewPage.displayName = 'SrListviewPage';

export default SrListviewPage;
